import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mainSiteUrl } from '@/mocks/categories';
import ArtigoNavbar from '@/components/feature/ArtigoNavbar';
import ArtigoFooter from '@/components/feature/ArtigoFooter';

function ArtigoContent() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL;
  const pageUrl = `${siteUrl}/artigo-cssbuyspreadsheet-guia-completo-2026`;
  const articleImage = 'https://readdy.ai/api/search-image?query=Elegant%20editorial%20photography%20of%20a%20modern%20workspace%20with%20laptop%20displaying%20product%20catalog%20surrounded%20by%20fashion%20items%20sneakers%20and%20accessories%20on%20a%20warm%20cream%20desk%20soft%20natural%20lighting%20minimalist%20aesthetic&width=1200&height=675&seq=artigo-v2-featured-main&orientation=landscape';

  return (
    <article className="pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-primary-500 transition-colors font-medium">Início</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-700">Guia CSSBuySpreadsheet</span>
        </nav>

        {/* Article Header */}
        <header className="mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-5">
            <i className="ri-book-open-line"></i>
            Guia Completo
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
            CSSBuySpreadsheet 2026 — Guia Completo para Comprar na China com Segurança e Economia
          </h1>
          <p className="text-lg text-foreground-600 leading-relaxed mb-6">
            Descubra como usar o <strong>CSSBuy Spreadsheet</strong> para encontrar os melhores produtos, comparar agentes de compra e economizar até 50% em sneakers, streetwear e acessórios importados da China.
          </p>
          <div className="flex items-center gap-3 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              Atualizado em Junho 2026
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              12 min de leitura
            </span>
          </div>
        </header>

        <div className="rounded-2xl overflow-hidden mb-12 w-full aspect-[16/9]">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20editorial%20photography%20of%20a%20modern%20workspace%20with%20laptop%20displaying%20product%20catalog%20surrounded%20by%20fashion%20items%20sneakers%20and%20accessories%20on%20a%20warm%20cream%20desk%20soft%20natural%20lighting%20minimalist%20aesthetic&width=1200&height=675&seq=artigo-v2-featured-main&orientation=landscape"
            alt="CSSBuySpreadsheet guia completo de compras na China"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="prose-custom">
          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">O Que é o CSSBuySpreadsheet?</h2>
            <p className="text-foreground-700 leading-relaxed mb-4">
              O <strong>CSSBuySpreadsheet</strong> é uma planilha online atualizada diariamente que reúne os melhores produtos disponíveis para compra através do agente CSSBuy. Funciona como um catálogo dinâmico onde compradores internacionais encontram desde <strong>sneakers</strong> de edição limitada até <strong>streetwear</strong> exclusivo, tudo com links diretos para compra segura na China.
            </p>
            <p className="text-foreground-700 leading-relaxed mb-4">
              Diferente de outros <strong>spreadsheets</strong> disponíveis na internet, o <strong>CSSBuy spreadsheet</strong> que recomendamos é verificado manualmente por uma equipe dedicada. Cada produto passa por uma checagem de qualidade, preço e disponibilidade antes de ser adicionado à planilha.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Por Que Usar um CSSBuy Spreadsheet?</h2>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8 my-8">
              <h3 className="font-heading text-xl font-bold text-foreground-900 mb-4">Vantagens do CSSBuy Spreadsheet</h3>
              <ul className="space-y-3">
                {[
                  { title: 'Economia de Tempo', desc: 'Todos os produtos já estão curados e organizados por categoria. Você encontra o que precisa em segundos.' },
                  { title: 'Preços Competitivos', desc: 'Nossa equipe compara preços entre diferentes vendedores e seleciona apenas as melhores ofertas.' },
                  { title: 'Links Verificados', desc: 'Cada link é testado manualmente. Nada de links quebrados ou produtos indisponíveis.' },
                  { title: 'Atualização Diária', desc: 'Novos produtos são adicionados todos os dias. As tendências mudam rapidamente, e nosso spreadsheet acompanha.' },
                  { title: 'Categorias Diversas', desc: 'De tênis a perfumes, 14 categorias cobrindo todos os estilos e preferências.' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mt-0.5">
                      <i className="ri-check-line text-xs"></i>
                    </span>
                    <div>
                      <strong className="text-foreground-900 text-sm">{item.title}:</strong>
                      <span className="text-foreground-600 text-sm ml-1">{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">CSSBuy vs Outros Agentes</h2>
            <div className="overflow-x-auto my-8 rounded-2xl border border-background-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100">
                    <th className="text-left p-4 font-semibold text-foreground-900">Característica</th>
                    <th className="text-left p-4 font-semibold text-foreground-900">CSSBuy</th>
                    <th className="text-left p-4 font-semibold text-foreground-900">Pandabuy</th>
                    <th className="text-left p-4 font-semibold text-foreground-900">Sugargoo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {[
                    ['Taxa de Serviço', '4-6%', '5-8%', '5-7%'],
                    ['Opções de Envio', '15+ linhas', '12+ linhas', '10+ linhas'],
                    ['Suporte ao Cliente', '24/7 Chat', 'Email + Chat', 'Chat'],
                    ['App Mobile', 'Sim', 'Sim', 'Apenas Web'],
                    ['Seguro de Pacote', 'Incluso', 'Opcional', 'Opcional'],
                  ].map((row, i) => (
                    <tr key={i} className="hover:bg-background-100/50 transition-colors">
                      <td className="p-4 font-medium text-foreground-900">{row[0]}</td>
                      <td className="p-4 text-foreground-700">{row[1]}</td>
                      <td className="p-4 text-foreground-700">{row[2]}</td>
                      <td className="p-4 text-foreground-700">{row[3]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Perguntas Frequentes</h2>
            <div className="space-y-4 my-8">
              {[
                { q: 'O CSSBuySpreadsheet é gratuito?', a: 'Sim! O acesso ao nosso spreadsheet e a todo o conteúdo do site é completamente gratuito. Nosso objetivo é ajudar a comunidade de compradores internacionais.' },
                { q: 'Com que frequência o spreadsheet é atualizado?', a: 'Nossa equipe atualiza o CSSBuy spreadsheet diariamente. Novos produtos são adicionados, links quebrados são removidos e os preços são verificados regularmente.' },
                { q: 'Preciso de conta no CSSBuy?', a: 'Recomendamos criar uma conta gratuita no CSSBuy para processar suas compras, mas você pode navegar pelo spreadsheet sem cadastro.' },
                { q: 'Os produtos são originais?', a: 'O spreadsheet inclui tanto produtos originais quanto réplicas de alta qualidade. Cada listagem é claramente identificada.' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="font-semibold text-foreground-900 text-sm pr-4">{faq.q}</span>
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-background-200 flex items-center justify-center group-open:rotate-45 transition-transform duration-200">
                      <i className="ri-add-line text-foreground-600 text-sm"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          <div className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center my-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">Pronto para Começar?</h2>
            <p className="text-primary-100 text-base mb-6 max-w-lg mx-auto">
              Acesse agora o CSSBuySpreadsheet atualizado e encontre os melhores produtos antes que esgotem.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full bg-background-50 text-primary-600 font-bold text-sm hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
              >
                CSSBuy Sheet
                <i className="ri-arrow-right-up-line"></i>
              </a>
              <Link to="/" className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full border-2 border-primary-300 text-background-50 font-bold text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                Voltar ao Início
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

          <div className="border-t border-background-200 pt-8 mt-12">
            <h3 className="font-heading text-lg font-bold text-foreground-900 mb-4">Continue Explorando</h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap font-medium">
                <i className="ri-home-line"></i>
                Página Inicial
              </Link>
              <button
                onClick={() => { navigate('/'); setTimeout(() => document.getElementById('categorias')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-grid-line"></i>
                Categorias
              </button>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-700 hover:bg-background-200 transition-colors whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line"></i>
                CSSBuy Sheet
              </a>
            </div>
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: 'CSSBuySpreadsheet 2026 — Guia Completo para Comprar na China com Segurança e Economia',
              description: 'Descubra como usar o CSSBuy Spreadsheet para encontrar os melhores produtos, comparar agentes de compra e economizar até 50% em sneakers, streetwear e acessórios importados da China.',
              image: articleImage,
              datePublished: '2026-06-01',
              dateModified: '2026-06-27',
              author: { '@type': 'Organization', name: 'CSSBuySpreadsheet' },
              publisher: { '@type': 'Organization', name: 'CSSBuySpreadsheet', logo: { '@type': 'ImageObject', url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/d367cf6745ee47f3fc82c76d3a54c50c.png' } },
              mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: [
                { '@type': 'Question', name: 'O CSSBuySpreadsheet é gratuito?', acceptedAnswer: { '@type': 'Answer', text: 'Sim! O acesso ao nosso spreadsheet e a todo o conteúdo do site é completamente gratuito.' } },
                { '@type': 'Question', name: 'Com que frequência o spreadsheet é atualizado?', acceptedAnswer: { '@type': 'Answer', text: 'Nossa equipe atualiza o CSSBuy spreadsheet diariamente.' } },
                { '@type': 'Question', name: 'Preciso de conta no CSSBuy?', acceptedAnswer: { '@type': 'Answer', text: 'Recomendamos criar uma conta gratuita no CSSBuy para processar suas compras.' } },
                { '@type': 'Question', name: 'Os produtos são originais?', acceptedAnswer: { '@type': 'Answer', text: 'O spreadsheet inclui tanto produtos originais quanto réplicas de alta qualidade.' } },
              ],
            }),
          }}
        />
      </div>
    </article>
  );
}

export default function ArtigoPage() {
  return (
    <main className="min-h-screen bg-background-50">
      <ArtigoNavbar />
      <ArtigoContent />
      <ArtigoFooter />
    </main>
  );
}